import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { openCodeAt } from '../../lib/code-reveal.js';
import {
  collapseEntries,
  locationsIn,
  parseLogMessage,
  useConsole,
  type LogEntry,
  type LogLevel,
  type SourceLocation,
} from '../../state/console.js';
import { IconButton } from '../ui/controls.js';
import { Icon, type IconName } from '../ui/icons.js';
import './console.css';

const LEVEL_ICON: Record<LogLevel, IconName> = {
  debug: 'console',
  info: 'info',
  warn: 'warning',
  error: 'error',
};

function time(t: number): string {
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

const goTo = (at: SourceLocation) => openCodeAt(at.path, at.line, at.column);

/** Text with every `file:line(:col)` turned into a link that opens the Code tab there. */
function Linked({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const loc of locationsIn(text)) {
    if (loc.index > last) parts.push(text.slice(last, loc.index));
    parts.push(
      <a
        key={loc.index}
        href="#"
        className="log-link"
        title={`Open ${loc.path} at line ${loc.line}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          goTo(loc);
        }}
      >
        {text.slice(loc.index, loc.index + loc.length)}
      </a>,
    );
    last = loc.index + loc.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function Toggle({ on, label, title, onClick }: { on: boolean; label: string; title: string; onClick: () => void }) {
  return (
    <button type="button" className={`console-flag${on ? ' is-on' : ''}`} aria-pressed={on} title={title} onClick={onClick}>
      {label}
    </button>
  );
}

/**
 * Engine / editor log (Unity's Console): level filters, search, Collapse
 * (identical messages anywhere as one line with a count), Clear on Play,
 * Clear on Build, Error Pause, a selected line's details with its stack
 * trace, and file:line links / double-click that open the Code tab there.
 * Filters and toggles are saved per user.
 */
export function Console({ actions }: { actions?: ReactNode }) {
  const entries = useConsole((s) => s.entries);
  const clear = useConsole((s) => s.clear);
  const prefs = useConsole((s) => s.prefs);
  const setPrefs = useConsole((s) => s.setPrefs);
  const { levels, filter, collapse } = prefs;
  const [selected, setSelected] = useState<number | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const logRef = useRef<HTMLDivElement | null>(null);

  const counts = useMemo(() => {
    const c = { debug: 0, info: 0, warn: 0, error: 0 };
    for (const e of entries) c[e.level] += e.count;
    return c;
  }, [entries]);

  const visible = useMemo(() => {
    const needle = filter.toLowerCase();
    const shown = entries.filter(
      (e) => levels[e.level] && (!needle || e.message.toLowerCase().includes(needle) || e.source.toLowerCase().includes(needle)),
    );
    return collapse ? collapseEntries(shown) : shown;
  }, [entries, levels, filter, collapse]);

  const current: LogEntry | undefined = selected === null ? undefined : (visible.find((e) => e.id === selected) ?? entries.find((e) => e.id === selected));
  const details = useMemo(() => (current ? parseLogMessage(current.message) : null), [current]);

  // Follow the end of the log unless the user scrolled up.
  useEffect(() => {
    const log = logRef.current;
    if (!log) return;
    const nearEnd = log.scrollHeight - log.scrollTop - log.clientHeight < 60;
    if (nearEnd) endRef.current?.scrollIntoView({ block: 'end' });
  }, [visible.length]);

  return (
    <div className="console">
      <div className="console-bar">
        <IconButton
          icon="trash"
          title="Clear console"
          onClick={() => {
            clear();
            setSelected(null);
          }}
        />
        <Toggle on={collapse} label="Collapse" title="Show identical messages once, with a count" onClick={() => setPrefs({ collapse: !collapse })} />
        <Toggle on={prefs.clearOnPlay} label="Clear on Play" title="Clear the log when Play starts" onClick={() => setPrefs({ clearOnPlay: !prefs.clearOnPlay })} />
        <Toggle on={prefs.clearOnBuild} label="Clear on Build" title="Clear the log when a build starts" onClick={() => setPrefs({ clearOnBuild: !prefs.clearOnBuild })} />
        <Toggle on={prefs.errorPause} label="Error Pause" title="Pause Play when an error is logged" onClick={() => setPrefs({ errorPause: !prefs.errorPause })} />
        <span className="bar-sep" />
        {(['error', 'warn', 'info', 'debug'] as const).map((level) => (
          <button
            key={level}
            type="button"
            className={`level-toggle level-${level}${levels[level] ? ' is-on' : ''}`}
            onClick={() => setPrefs({ levels: { ...levels, [level]: !levels[level] } })}
            title={`${levels[level] ? 'Hide' : 'Show'} ${level}`}
          >
            <Icon name={LEVEL_ICON[level]} size={13} />
            {counts[level]}
          </button>
        ))}
        <div className="search-input compact">
          <Icon name="search" size={12} />
          <input value={filter} onChange={(e) => setPrefs({ filter: e.target.value })} placeholder="Filter" aria-label="Filter log" />
        </div>
        <span className="bar-spacer" />
        {actions}
      </div>
      <div className="console-log" role="log" ref={logRef}>
        {visible.length === 0 && (
          <p className="console-empty muted">
            {entries.length ? 'No lines match the filter.' : 'Engine and script logs appear here.'}
          </p>
        )}
        {visible.map((e) => {
          const parsed = e.message.includes('\n') ? parseLogMessage(e.message) : null;
          const firstLine = (parsed ? parsed.text : e.message).split('\n')[0] ?? '';
          const more = parsed ? parsed.stack.length > 0 || parsed.text.includes('\n') : false;
          return (
            <div
              key={e.id}
              className={`log-line level-${e.level}${selected === e.id ? ' is-selected' : ''}`}
              onClick={() => setSelected(selected === e.id ? null : e.id)}
              onDoubleClick={() => {
                const at = parseLogMessage(e.message).location;
                if (at) goTo(at);
              }}
              title={more ? 'Click for details and the stack trace; double-click opens the source' : undefined}
            >
              <Icon name={LEVEL_ICON[e.level]} size={13} className="log-icon" />
              <span className="log-time mono">{time(e.time)}</span>
              <span className="log-source mono">{e.source}</span>
              <span className="log-msg">
                <Linked text={firstLine} />
                {more && <span className="log-more"> …</span>}
              </span>
              {e.count > 1 && <span className="log-count">{e.count}</span>}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {current && details && (
        <div className={`console-details level-${current.level}`}>
          <div className="console-details-head">
            <Icon name={LEVEL_ICON[current.level]} size={12} />
            <span className="mono">{current.source}</span>
            <span className="muted mono">{time(current.time)}</span>
            {current.count > 1 && <span className="log-count">{current.count}×</span>}
            <span className="bar-spacer" />
            {details.location && (
              <button type="button" className="ghost small" onClick={() => goTo(details.location!)} title="Open in the Code tab">
                <Icon name="code" size={12} /> {details.location.path}:{details.location.line}
              </button>
            )}
            <IconButton icon="copy" title="Copy message" size={12} onClick={() => void navigator.clipboard?.writeText(current.message)} />
            <IconButton icon="x" title="Close details" size={12} onClick={() => setSelected(null)} />
          </div>
          <pre className="console-details-text">
            <Linked text={details.text} />
          </pre>
          {details.stack.length > 0 && (
            <ol className="console-stack" aria-label="Stack trace">
              {details.stack.map((f, i) => (
                <li key={i}>
                  <span className="muted">at </span>
                  <span className="console-stack-fn">{f.function}</span>{' '}
                  <a
                    href="#"
                    className="log-link"
                    onClick={(ev) => {
                      ev.preventDefault();
                      goTo(f);
                    }}
                  >
                    {f.path}:{f.line}:{f.column}
                  </a>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
